import { useMemo } from 'react'
import { useSearch } from '@tanstack/react-router'
import { useChannels, type Channel } from '@/api/endpoints/channels'

export interface ActiveChannelState {
  /** Full list of channels (may be empty while loading). */
  channels: Channel[]
  /** Active channel object, or undefined while loading or when no channels exist. */
  active: Channel | undefined
  /** Whether the channels list has loaded at least once. */
  isLoading: boolean
  /** Returned error from the list query, if any. */
  error: unknown
}

/**
 * Resolves the active channel from an explicit `slug` (usually from URL),
 * falling back to the user's default, then to the first channel in the
 * list. Reading the channels list is cached by React Query — calling this
 * hook across the app is cheap.
 */
export function useActiveChannel(slug: string | null | undefined): ActiveChannelState {
  const { data: channels = [], isLoading, error } = useChannels()

  const active = useMemo(() => {
    if (channels.length === 0) return undefined
    if (slug) return channels.find((c) => c.slug === slug) ?? channels[0]
    return channels.find((c) => c.is_default) ?? channels[0]
  }, [channels, slug])

  return { channels, active, isLoading, error }
}

/**
 * Reactive flavour of ``useActiveChannel`` that reads ``?channel=`` from
 * the current URL. Used by every channel-aware page (Analytics, Safety,
 * Ranking, ...) so the sidebar's channel switcher (which writes that
 * search param via ``to: '.'``) actually triggers a re-render with the
 * new context. Without this they'd ignore the URL change and stay on the
 * default channel.
 */
export function useActiveChannelFromUrl(): ActiveChannelState {
  const search = useSearch({ strict: false })
  const channel = (search as Record<string, unknown>)['channel']
  const slug = typeof channel === 'string' ? channel : null
  return useActiveChannel(slug)
}
